class AnniversaryCreatorVoices extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return
    this.abortController = new AbortController()
    const { signal } = this.abortController
    this.cards = [...this.querySelectorAll('[data-anniversary-creator]')]
    this.viewport = this.querySelector('[data-creator-viewport]')
    this.track = this.querySelector('[data-creator-track]')
    this.previous = this.querySelector('[data-creator-previous]')
    this.next = this.querySelector('[data-creator-next]')
    this.dots = [...this.querySelectorAll('[data-creator-dot]')]
    this.mediaPlayers = new Map()
    this.pointerSelecting = false
    this.touchScrolling = false
    this.scrollEndSupported = this.viewport && 'onscrollend' in this.viewport
    this.cardAnimations = new Set()
    this.selectionSource = 'explicit'
    this.activeIndex = Math.min(this.activeIndex ?? 0, Math.max(0, this.cards.length - 1))
    this.previous?.addEventListener('click', () => this.select(this.activeIndex - 1, true, true), {
      signal,
    })
    this.next?.addEventListener('click', () => this.select(this.activeIndex + 1, true, true), {
      signal,
    })
    this.addEventListener(
      'click',
      (event) => {
        this.pointerSelecting = false
        if (event.target.closest('[data-creator-media]')) return
        if (this.suppressClick && event.detail !== 0) {
          this.suppressClick = false
          event.preventDefault()
          return
        }
        const dot = event.target.closest('[data-creator-dot]')
        if (dot && this.contains(dot)) this.select(Number(dot.dataset.creatorDot), true, true)
        const trigger = event.target.closest('[data-creator-select]')
        if (!trigger || !this.contains(trigger)) return
        const card = trigger.closest('[data-anniversary-creator]')
        this.select(this.cards.indexOf(card), true, trigger.hasAttribute('data-creator-play'))
      },
      { signal },
    )
    this.addEventListener(
      'keydown',
      (event) => {
        this.pointerSelecting = false
        if (
          event.target.closest('[data-creator-media]') ||
          !event.target.closest('[data-creator-viewport]')
        )
          return
        const targets = {
          ArrowLeft: this.activeIndex - 1,
          ArrowRight: this.activeIndex + 1,
          Home: 0,
          End: this.cards.length - 1,
        }
        if (!(event.key in targets)) return
        event.preventDefault()
        this.select(targets[event.key], true, true)
      },
      { signal },
    )
    this.addEventListener(
      'focusin',
      (event) => {
        if (this.pointerSelecting || event.target.closest('[data-creator-media]')) return
        const card = event.target.closest('[data-anniversary-creator]')
        if (card && this.contains(card) && this.cards.indexOf(card) !== this.activeIndex) {
          this.select(this.cards.indexOf(card))
        }
      },
      { signal },
    )
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId)
        if (index !== -1) this.select(index, false)
      },
      { signal },
    )
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (event.target === this || event.target.contains?.(this)) this.disconnectedCallback()
      },
      { signal },
    )
    document.addEventListener(
      'anniversary:creator-play',
      (event) => {
        if (event.detail?.owner !== this) this.pauseMedia()
      },
      { signal },
    )
    this.viewport?.addEventListener(
      'pointerdown',
      (event) => {
        this.cancelScrollSettlement()
        this.pointerSelecting = true
        this.suppressClick = false
        this.pointerStart = {
          x: event.clientX,
          y: event.clientY,
          id: event.pointerId,
          media: Boolean(event.target.closest('[data-creator-media]')),
        }
      },
      { signal },
    )
    this.viewport?.addEventListener(
      'pointermove',
      (event) => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return
        const horizontal = Math.abs(this.pointerStart.x - event.clientX)
        const vertical = Math.abs(this.pointerStart.y - event.clientY)
        this.pointerStart.horizontal = horizontal > 8 && horizontal > vertical
      },
      { signal, passive: true },
    )
    this.viewport?.addEventListener(
      'pointerup',
      (event) => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return
        const delta = this.pointerStart.x - event.clientX
        const vertical = Math.abs(this.pointerStart.y - event.clientY)
        const media = this.pointerStart.media
        this.pointerStart = null
        if (media) {
          // A cancelled horizontal touch is a native pan; a completed media
          // pointer gesture belongs to the player's controls, including seek.
          this.pointerSelecting = false
          return
        }
        if (Math.abs(delta) > 40 && Math.abs(delta) > vertical) {
          this.suppressClick = true
          this.select(this.activeIndex + Math.sign(delta), true, true)
        }
      },
      { signal },
    )
    this.viewport?.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) {
          this.cancelScrollSettlement()
          this.selectionSource = 'manual'
        }
      },
      { signal, passive: true },
    )
    this.viewport?.addEventListener(
      'pointercancel',
      () => {
        if (!this.pointerStart) return
        if (this.pointerStart.horizontal) this.selectionSource = 'manual'
        this.pointerSelecting = false
        this.pointerStart = null
      },
      { signal },
    )
    this.viewport?.addEventListener(
      'touchstart',
      () => {
        this.touchScrolling = true
        this.cancelScrollSettlement()
      },
      { signal, passive: true },
    )
    for (const type of ['touchend', 'touchcancel']) {
      this.viewport?.addEventListener(
        type,
        (event) => {
          this.touchScrolling = Boolean(event.touches?.length)
          if (!this.touchScrolling) this.scheduleScrollSettlement()
        },
        { signal, passive: true },
      )
    }
    this.viewport?.addEventListener(
      'scroll',
      () => {
        if (this.selectionSource !== 'manual') return
        this.scheduleScrollSettlement()
        if (this.scrollFrame) return
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null
          this.selectFromScroll()
        })
      },
      { signal, passive: true },
    )
    this.viewport?.addEventListener('scrollend', () => this.settleFromScroll(), { signal })
    this.viewportWidth = this.viewport?.clientWidth
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.viewport?.clientWidth
      if (width && width !== this.viewportWidth) {
        this.viewportWidth = width
        this.select(this.activeIndex, false)
      }
    })
    if (this.viewport) this.resizeObserver.observe(this.viewport)
    this.select(this.activeIndex, false)
    this.observeMedia(signal)
    this.setAttribute('data-enhanced', '')
  }

  cancelScrollSettlement() {
    clearTimeout(this.settleTimer)
    this.settleTimer = null
  }

  scheduleScrollSettlement() {
    this.cancelScrollSettlement()
    if (
      this.scrollEndSupported ||
      this.touchScrolling ||
      this.pointerSelecting ||
      this.selectionSource !== 'manual' ||
      !matchMedia('(max-width: 749px)').matches
    )
      return
    // Older Safari has no scrollend. Wait for touch release and then a short
    // quiet period; each inertial scroll postpones completion.
    this.settleTimer = setTimeout(() => this.settleFromScroll(), 100)
  }

  settleFromScroll() {
    this.cancelScrollSettlement()
    if (this.selectionSource !== 'manual' || this.abortController?.signal.aborted) return
    this.selectFromScroll()
    // Native scrolling owns the gesture and inertia. Only settle once it
    // finishes, then retain this target through the settling scrollend.
    if (
      matchMedia('(max-width: 749px)').matches &&
      !this.pointerSelecting &&
      !this.touchScrolling
    ) {
      this.selectionSource = 'explicit'
      cancelAnimationFrame(this.scrollFrame)
      this.scrollFrame = null
      this.alignActiveCard()
    }
  }

  selectFromScroll() {
    if (this.selectionSource !== 'manual' || !this.viewport || !this.cards.length) return
    const inset = parseFloat(getComputedStyle(this.track).paddingLeft) || 0
    const left = this.viewport.scrollLeft
    const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth)
    let closest = this.activeIndex
    let distance = Infinity
    this.cards.forEach((card, index) => {
      const nextDistance = Math.abs(card.offsetLeft - inset - left)
      if (nextDistance < distance) {
        closest = index
        distance = nextDistance
      }
    })
    if (left <= 1) closest = 0
    else if (maximum > 0 && left >= maximum - 1) closest = this.cards.length - 1
    // Manual selection follows the gesture; scrolling again here would fight it.
    if (closest !== this.activeIndex) this.select(closest, true, false, false)
  }

  select(index, animate = true, playSelected = false, align = true) {
    if (!this.viewport || !this.cards?.length) return
    if (align) {
      this.cancelScrollSettlement()
      // A clamped end position can represent several cards. Keep explicit intent
      // until a real horizontal gesture takes over, regardless of scrollend count.
      this.selectionSource = 'explicit'
      cancelAnimationFrame(this.scrollFrame)
      this.scrollFrame = null
    }
    const selected = Math.max(0, Math.min(this.cards.length - 1, index))
    if (selected === this.activeIndex && animate) {
      if (align) this.alignActiveCard(animate)
      if (playSelected) this.playMedia(this.cards[selected])
      return
    }
    const shouldAnimate =
      animate &&
      selected !== this.activeIndex &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches
    const previousRects = shouldAnimate
      ? this.cards.map((card) => card.getBoundingClientRect())
      : null
    this.cancelCardAnimations()
    if (selected !== this.activeIndex) this.pauseMedia(this.cards[selected])
    this.activeIndex = selected
    this.cards.forEach((card, position) => {
      card.setAttribute('data-active', String(position === selected))
      card
        .querySelector('[data-creator-select]')
        ?.setAttribute('aria-pressed', String(position === selected))
    })
    this.dots.forEach((dot, position) =>
      dot.setAttribute('aria-pressed', String(position === selected)),
    )
    if (this.previous) this.previous.disabled = selected === 0
    if (this.next) this.next.disabled = selected === this.cards.length - 1
    if (previousRects) {
      this.cards.forEach((card, position) => {
        const before = previousRects[position]
        const after = card.getBoundingClientRect()
        if (!before.width || !after.width || !before.height || !after.height) return
        const animation = card.animate(
          [
            {
              transform: `translate(${before.left - after.left}px, ${before.top - after.top}px) scale(${before.width / after.width}, ${before.height / after.height})`,
            },
            { transform: 'none' },
          ],
          { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        )
        this.cardAnimations.add(animation)
        animation.onfinish = () => this.cardAnimations.delete(animation)
      })
    }
    if (align) this.alignActiveCard(animate)
    if (playSelected) this.playMedia(this.cards[selected])
  }

  alignActiveCard(animate = true) {
    const card = this.cards[this.activeIndex]
    if (!card || !this.viewport) return
    const inset = parseFloat(getComputedStyle(this.track).paddingLeft) || 0
    const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth)
    const left = Math.max(0, Math.min(maximum, card.offsetLeft - inset))
    this.viewport.scrollTo({
      left,
      behavior:
        animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
    })
  }

  cancelCardAnimations() {
    this.cardAnimations?.forEach((animation) => animation.cancel())
    this.cardAnimations?.clear()
  }

  observeMedia(signal) {
    if (typeof IntersectionObserver === 'undefined' || !this.viewport) return
    const verticalMargin = 250
    const nearbyCards = new Set()
    let nearViewport = false
    const warmNearbyMedia = () => {
      if (signal.aborted || !nearViewport || !nearbyCards.size) return
      // IO notifications can arrive after a scroll. Recheck the actual media
      // area so a late horizontal entry cannot use an outdated vertical gate.
      const { top, bottom, width, height } = this.viewport.getBoundingClientRect()
      if (
        width <= 0 ||
        height <= 0 ||
        top >= innerHeight + verticalMargin ||
        bottom <= -verticalMargin
      )
        return
      for (const card of nearbyCards) {
        if (!this.prepareMedia(card, true)) continue
        nearbyCards.delete(card)
        this.mediaObserver.unobserve(card)
      }
    }
    this.mediaObserver = new IntersectionObserver(
      (entries) => {
        if (signal.aborted) return
        for (const entry of entries) {
          if (entry.isIntersecting) nearbyCards.add(entry.target)
          else nearbyCards.delete(entry.target)
        }
        warmNearbyMedia()
      },
      { root: this.viewport, rootMargin: '0px 400px', threshold: 0.01 },
    )
    this.proximityObserver = new IntersectionObserver(
      (entries) => {
        if (signal.aborted) return
        for (const entry of entries) nearViewport = entry.isIntersecting
        warmNearbyMedia()
      },
      { rootMargin: `${verticalMargin}px 0px`, threshold: 0.01 },
    )
    this.proximityObserver.observe(this.viewport)
    this.cards.forEach((card) => {
      if (card.querySelector('template[data-anniversary-video]')?.content.querySelector('video')) {
        this.mediaObserver.observe(card)
      }
    })
  }

  prepareMedia(card, hostedOnly = false) {
    const template = card?.querySelector('template[data-anniversary-video]')
    const slot = card?.querySelector('[data-creator-media]')
    if (!template || !slot || (hostedOnly && !template.content.querySelector('video'))) return
    const index = this.cards.indexOf(card)
    let state = this.mediaPlayers.get(card)
    if (!state) {
      slot.replaceChildren(template.content.cloneNode(true))
      state = {
        slot,
        poster: card.querySelector('[data-creator-select]'),
        error: card.querySelector('[data-creator-media-error]'),
        video: slot.querySelector('video'),
        owner: new AbortController(),
        request: 0,
      }
      this.mediaPlayers.set(card, state)
      state.video?.addEventListener(
        'play',
        () => {
          if (state.video.paused || state.owner.signal.aborted) return
          if (this.activeIndex !== index) this.select(index)
          this.activateMedia(card, state)
        },
        { signal: state.owner.signal },
      )
      state.video?.addEventListener(
        'error',
        () => {
          if (!state.slot.hidden) this.mediaFailed(card, state)
        },
        { signal: state.owner.signal },
      )
      state.video?.addEventListener('pause', () => card.setAttribute('data-playing', 'false'), {
        signal: state.owner.signal,
      })
    }
    if (state.video && state.video.preload !== 'auto') {
      state.video.preload = 'auto'
      state.video.load()
    }
    return state
  }

  playMedia(card) {
    const state = this.prepareMedia(card)
    if (!state) return
    const index = this.cards.indexOf(card)
    if (index !== this.activeIndex) this.select(index)
    const focusPlayer = document.activeElement === state.poster
    this.activateMedia(card, state)
    const request = ++state.request
    state.video?.play()?.catch((error) => {
      if (request !== state.request || error.name === 'AbortError') return
      this.mediaFailed(card, state)
    })
    if (focusPlayer)
      (state.video ?? state.slot.querySelector('iframe'))?.focus({ preventScroll: true })
  }

  activateMedia(card, state) {
    this.pauseMedia(card)
    document.dispatchEvent(new CustomEvent('anniversary:creator-play', { detail: { owner: this } }))
    state.slot.hidden = false
    if (state.poster) state.poster.hidden = true
    if (state.error) state.error.hidden = true
    card.setAttribute('data-playing', 'true')
  }

  mediaFailed(card, state) {
    if (
      state.owner.signal.aborted ||
      this.mediaPlayers.get(card) !== state ||
      this.cards[this.activeIndex] !== card
    )
      return
    const restoreFocus = state.slot.contains(document.activeElement)
    state.video?.pause()
    state.slot.hidden = true
    if (state.poster) state.poster.hidden = false
    if (state.error) state.error.hidden = false
    card.setAttribute('data-playing', 'false')
    if (restoreFocus) state.poster?.focus({ preventScroll: true })
  }

  pauseMedia(except = null) {
    this.mediaPlayers?.forEach((state, card) => {
      if (card === except) return
      state.request += 1
      state.video?.pause()
      state.slot.hidden = true
      if (state.poster) state.poster.hidden = false
      if (state.error) state.error.hidden = true
      card.setAttribute('data-playing', 'false')
      if (!state.video) {
        state.owner.abort()
        state.slot.replaceChildren()
        this.mediaPlayers.delete(card)
      }
    })
  }

  disconnectedCallback() {
    this.cancelScrollSettlement()
    this.abortController?.abort()
    this.abortController = null
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.mediaObserver?.disconnect()
    this.proximityObserver?.disconnect()
    this.pointerStart = null
    cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = null
    this.cancelCardAnimations()
    this.pauseMedia()
    this.mediaPlayers?.forEach((state) => {
      state.owner.abort()
      state.slot.replaceChildren()
    })
    this.mediaPlayers?.clear()
    this.pointerSelecting = false
    this.touchScrolling = false
    this.removeAttribute('data-enhanced')
  }
}

if (!customElements.get('anniversary-creator-voices')) {
  customElements.define('anniversary-creator-voices', AnniversaryCreatorVoices)
}
